import React, { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { useSelector } from "react-redux";
import { selectUserID } from "../../../redux/IchthusSlice";
import { FaTimes, FaSave, FaFlask } from "react-icons/fa";
import Loader from "../../loader/Loader";
import Pagination from "../Pagination";
import {
  Loader2,
  PackageCheck,
  Layers,
  ChevronDown,
  Calculator,
  AlertCircle,
  Warehouse,
  Calendar,
  Hash,
  FileText,
  MapPin,
  Tag,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Info,
  ArrowRight,
  ArrowDownToLine,
  // ✨ NEW icons
  FlaskConical,
  CircleDashed,
  CircleAlert,
  Weight,
  ListChecks,
  TriangleAlert,
  Target,
  RotateCcw,
  Zap,
  Pencil,
  Package,
  Search, 
  Check
} from "lucide-react";
import { domain } from "../../../security";

// ─── STYLE CONSTANTS ──────────────────────────────────────────────────────────
const inputClass =
  "block w-full rounded-xl border border-gray-300 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-900 transition-all focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-200 outline-none";
const labelClass =
  "mb-1 ml-0.5 block text-[10px] font-black uppercase tracking-widest text-gray-500";

const getColorClasses = (color) => {
  const map = {
    blue: {
      text: "text-blue-500",
      border: "border-blue-200",
      ring: "focus:ring-blue-200 focus:border-blue-400",
      bar: "bg-blue-400",
    },
    amber: {
      text: "text-amber-500",
      border: "border-amber-200",
      ring: "focus:ring-amber-200 focus:border-amber-400",
      bar: "bg-amber-400",
    },
    slate: {
      text: "text-slate-500",
      border: "border-slate-200",
      ring: "focus:ring-slate-200 focus:border-slate-400",
      bar: "bg-slate-400",
    },
    emerald: {
      text: "text-emerald-500",
      border: "border-emerald-200",
      ring: "focus:ring-emerald-200 focus:border-emerald-400",
      bar: "bg-emerald-400",
    },
    violet: {
      text: "text-violet-500",
      border: "border-violet-200",
      ring: "focus:ring-violet-200 focus:border-violet-400",
      bar: "bg-violet-400",
    },
    rose: {
      text: "text-rose-500",
      border: "border-rose-200",
      ring: "focus:ring-rose-200 focus:border-rose-400",
      bar: "bg-rose-400",
    },
    yellow: {
      text: "text-yellow-500",
      border: "border-yellow-200",
      ring: "focus:ring-yellow-200 focus:border-yellow-400",
      bar: "bg-yellow-400",
    },
    orange: {
      text: "text-orange-500",
      border: "border-orange-200",
      ring: "focus:ring-orange-200 focus:border-orange-400",
      bar: "bg-orange-400",
    },
    cyan: {
      text: "text-cyan-500",
      border: "border-cyan-200",
      ring: "focus:ring-cyan-200 focus:border-cyan-400",
      bar: "bg-cyan-400",
    },
  };
  return map[color] || map.slate;
};

const fmt = (val) =>
  new Intl.NumberFormat("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(val ?? 0);

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "—";

// ─── WEIGHT BAR ───────────────────────────────────────────────────────────────
const WeightBar = ({ label, value, total, barColor }) => {
  const pct = total > 0 ? Math.min(100, (value / total) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[10px] font-bold text-gray-500 uppercase tracking-wide">
        <span>{label}</span>
        <span>{pct.toFixed(1)}%</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${barColor}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

// ─── CLICKABLE CONSUMPTION SUMMARY ────────────────────────────────────────────
const ConsumptionSummary = ({ tag, resolution, onClick }) => {
  if (!tag.linkedProducts || tag.linkedProducts.length === 0) return null;

  const req = resolution?.requested || 0;
  const avail = resolution?.totalAvailable || 0;
  const remaining = avail - req;
  const deficit = resolution?.remaining > 0 ? resolution.remaining : 0;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full mt-1 rounded border flex flex-col gap-1 transition-all ${
        deficit > 0
          ? "bg-red-50 border-red-200 hover:bg-red-100"
          : "bg-slate-50 border-slate-200 hover:bg-slate-100 hover:border-indigo-300"
      }`}
    >
      <div className={`w-full flex items-center justify-between px-2 py-1 text-[10px]`}>
        <div className="flex items-center gap-1.5 text-slate-600 font-medium">
          <span>
            Stock: <b className="text-slate-800">{fmt(avail)}</b>
          </span>
          <span className="text-slate-300">-</span>
          <span>
            Use:{" "}
            <b className={req > 0 ? "text-indigo-600" : "text-slate-400"}>
              {fmt(req)}
            </b>
          </span>
          <span className="text-slate-300">=</span>
          <span
            className={`font-bold ${remaining < 0 ? "text-red-600" : "text-emerald-600"}`}
          >
            {fmt(remaining)}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {deficit > 0 && (
            <span className="text-red-600 font-semibold bg-white border border-red-200 px-1 py-0.5 rounded text-[8px] uppercase tracking-wider flex items-center gap-0.5">
              <XCircle size={8} /> {fmt(deficit)}
            </span>
          )}
          <Info
            size={12}
            className={deficit > 0 ? "text-red-400" : "text-indigo-400"}
          />
        </div>
      </div>
    </button>
  );
};

const ConsumptionSummaryProduct = ({ tag, resolution, value, onClick }) => {
  const req = resolution?.requested || 0;
  const avail = resolution?.totalAvailable || 0;
  const remaining = avail - req;
  //const deficit = resolution?.remaining > 0 ? resolution.remaining : 0;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full mt-1 rounded border flex flex-col gap-1 transition-all ${
        remaining < 0
          ? "bg-red-50 border-red-200 hover:bg-red-100"
          : "bg-slate-50 border-slate-200 hover:bg-slate-100 hover:border-indigo-300"
      }`}
    >
      <div className={`w-full flex items-center justify-between px-2 py-1 text-[10px]`}>
        <div className="flex items-center gap-1.5 text-slate-600 font-medium">
          <span>
            Stock: <b className="text-slate-800">{fmt(avail)}</b>
          </span>
          <span className="text-slate-300">-</span>
          <span>
            Use:{" "}
            <b className={req > 0 ? "text-indigo-600" : "text-slate-400"}>
              {fmt(req)}
            </b>
          </span>
          <span className="text-slate-300">=</span>
          <span
            className={`font-bold ${remaining < 0 ? "text-red-600" : "text-emerald-600"}`}
          >
            {fmt(remaining)}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {remaining < 0 && (
            <span className="text-red-600 font-semibold bg-white border border-red-200 px-1 py-0.5 rounded text-[8px] uppercase tracking-wider flex items-center gap-0.5">
              <XCircle size={8} /> {fmt(remaining)}
            </span>
          )}
          <Info
            size={12}
            className={remaining < 0 ? "text-red-400" : "text-indigo-400"}
          />
        </div>
      </div>
    </button>
  );
};

// ─── SUPER DETAIL MODAL ───────────────────────────────────────────────────────
const ConsumptionDetailModal = ({ tag, resolution, onClose }) => {
  if (!tag || !resolution) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[85vh]">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-indigo-50/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">
              <Calculator size={18} />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-800 leading-tight">
                Allocation Details: {tag.tagName}
              </h3>
              <p className="text-xs font-semibold text-indigo-600 mt-0.5">
                Target Request: {fmt(resolution.requested)} {tag.unit}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-red-500 bg-white rounded-lg border border-slate-200 hover:border-red-200 transition-colors"
          >
            <FaTimes size={14} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto bg-slate-50 flex-1 space-y-4">
          {resolution.splits.map((split) => {
            const isUsed = split.quantity > 0;
            return (
              <div
                key={split.productId}
                className={`bg-white rounded-xl border p-4 shadow-sm transition-all ${isUsed ? "border-emerald-200 ring-1 ring-emerald-50" : "border-slate-200 opacity-70"}`}
              >
                <div className="flex justify-between items-start mb-3 border-b border-slate-50 pb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider ${isUsed ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}
                      >
                        Priority {split.priority}
                      </span>
                      <h4
                        className={`font-bold text-sm ${isUsed ? "text-emerald-900" : "text-slate-700"}`}
                      >
                        {split.productName}
                      </h4>
                    </div>
                    <p className="text-[10px] text-slate-400 font-mono mt-1">
                      Rate: 1 {tag.unit} = {fmt(split.rateToBase)}{" "}
                      {split.baseUom}
                    </p>
                  </div>
                  {isUsed ? (
                    <span className="text-xs font-black text-emerald-600 bg-emerald-50 border border-emerald-100 px-2 py-1 rounded-lg">
                      Using {fmt(split.quantity)} {tag.unit}
                    </span>
                  ) : (
                    <span className="text-xs font-bold text-slate-400 bg-slate-50 border border-slate-100 px-2 py-1 rounded-lg">
                      Skipped
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-2 text-center items-center">
                  <div className="bg-slate-50 p-2 rounded-lg border border-slate-100">
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">
                      Start Stock
                    </p>
                    <p className="text-sm font-bold text-slate-700">
                      {fmt(split.availableBase)}
                    </p>
                    <p className="text-[10px] font-bold text-slate-400">
                      {split.baseUom}
                    </p>
                  </div>
                  <div className="flex justify-center text-slate-300">
                    <ArrowRight size={18} />
                  </div>
                  <div
                    className={`p-2 rounded-lg border ${isUsed ? "bg-amber-50 border-amber-100" : "bg-slate-50 border-slate-100"}`}
                  >
                    <p
                      className={`text-[9px] font-black uppercase tracking-widest mb-1 ${isUsed ? "text-amber-500" : "text-slate-400"}`}
                    >
                      Deduction
                    </p>
                    <p
                      className={`text-sm font-black ${isUsed ? "text-amber-600" : "text-slate-400"}`}
                    >
                      -{fmt(split.consumedBase)}
                    </p>
                    <p
                      className={`text-[10px] font-bold ${isUsed ? "text-amber-500" : "text-slate-400"}`}
                    >
                      {split.baseUom}
                    </p>
                  </div>
                  <div className="col-span-3 flex justify-center mt-1 text-slate-300">
                    <ArrowDownToLine size={14} />
                  </div>
                  <div className="col-start-2 bg-emerald-50 p-2 rounded-lg border border-emerald-100 shadow-inner">
                    <p className="text-[9px] font-black uppercase tracking-widest text-emerald-500 mb-1">
                      Remaining Stock
                    </p>
                    <p className="text-base font-black text-emerald-700">
                      {fmt(split.availableBase - split.consumedBase)}
                    </p>
                    <p className="text-[10px] font-bold text-emerald-600">
                      {split.baseUom}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}

          {resolution.remaining > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between shadow-sm">
              <div className="flex items-center gap-3 text-red-600">
                <AlertCircle size={24} />
                <div>
                  <p className="font-bold text-sm">Insufficient Stock</p>
                  <p className="text-xs text-red-500 mt-0.5">
                    We ran out of stock across all linked priorities.
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-black text-red-700">
                  {fmt(resolution.remaining)}
                </p>
                <p className="text-[10px] font-bold uppercase tracking-widest text-red-500">
                  {tag.unit} short
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ✨ NEW: DO ITEM PRODUCTION OVERVIEW PANEL
// Shows all items in the selected DO and their production status side-by-side.
// "Complete" = item has at least one production record.
// "Partial"  = item is the currently selected one (in-progress).
// "None"     = no production exists for this item yet.
// ─────────────────────────────────────────────────────────────────────────────
const DOItemsProductionOverview = ({
  selectedDO,
  doItemProductionMap,
  selectedItemId,
  isFetchingProductions,
}) => {
  if (!selectedDO) return null;
  const items = selectedDO.deliveryOrderItems?.filter(i => i.isBom) || [];
  if (items.length === 0) return null;

  const coveredCount = items.filter(
    (i) => (doItemProductionMap[i.id] || []).length > 0,
  ).length;

  const overallStatus =
    coveredCount === 0
      ? "none"
      : coveredCount >= items.length
        ? "complete"
        : "partial";

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-black uppercase tracking-widest text-gray-500 flex items-center gap-2">
          <ListChecks size={13} className="text-indigo-400" />
          DO Production Overview
        </h4>
        {isFetchingProductions ? (
          <span className="flex items-center gap-1 text-[10px] text-indigo-400 font-semibold">
            <Loader2 size={10} className="animate-spin" /> Checking…
          </span>
        ) : (
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${
              overallStatus === "complete"
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : overallStatus === "partial"
                  ? "bg-amber-50 text-amber-700 border-amber-200"
                  : "bg-gray-50 text-gray-500 border-gray-200"
            }`}
          >
            {overallStatus === "complete" ? (
              <CheckCircle2 size={9} />
            ) : overallStatus === "partial" ? (
              <CircleAlert size={9} />
            ) : (
              <CircleDashed size={9} />
            )}
            {overallStatus === "complete"
              ? "All Items Produced"
              : overallStatus === "partial"
                ? `${coveredCount}/${items.length} Items Produced`
                : "No Productions Yet"}
          </span>
        )}
      </div>

      {/* Items grid */}
      <div className="grid grid-cols-1 gap-2">
        {items.map((item) => {
          const itemProds = doItemProductionMap[item.id] || [];
          const isSelected = String(item.id) === String(selectedItemId);
          const hasProduction = itemProds.length > 0;
          const totalWeight = itemProds.reduce(
            (s, p) => s + (p.totalActualWeight || 0),
            0,
          );

          return (
            <div
              key={item.id}
              className={`flex items-center justify-between px-3 py-2.5 rounded-lg border text-xs transition-all ${
                isSelected
                  ? "bg-indigo-50 border-indigo-200 ring-1 ring-indigo-100"
                  : hasProduction
                    ? "bg-emerald-50 border-emerald-200"
                    : "bg-gray-50 border-gray-200"
              }`}
            >
              {/* Left: item name + indicator */}
              <div className="flex items-center gap-2 min-w-0 flex-1">
                {hasProduction ? (
                  <CheckCircle2
                    size={13}
                    className="text-emerald-500 shrink-0"
                  />
                ) : isSelected ? (
                  <FlaskConical
                    size={13}
                    className="text-indigo-500 shrink-0"
                  />
                ) : (
                  <CircleDashed size={13} className="text-gray-300 shrink-0" />
                )}
                <div className="min-w-0">
                  <p
                    className={`font-bold truncate ${
                      isSelected
                        ? "text-indigo-900"
                        : hasProduction
                          ? "text-emerald-900"
                          : "text-gray-500"
                    }`}
                  >
                    {item.productName}
                  </p>
                  <p className="text-[9px] text-gray-400 font-semibold">
                    Qty:{" "}
                    <span className="text-gray-600 font-bold">
                      {item.quantityDelivered ?? item.quantity ?? item.quantityReceived}
                    </span>
                  </p>
                </div>
              </div>

              {/* Right: production count or status */}
              <div className="text-right shrink-0 ml-3">
                {isSelected && !hasProduction && (
                  <span className="text-[9px] font-black text-indigo-600 bg-indigo-100 px-1.5 py-0.5 rounded uppercase tracking-wider">
                    In Progress
                  </span>
                )}
                {hasProduction && (
                  <div>
                    <p className="text-emerald-600 font-black text-[10px]">
                      {itemProds.length} production(s)
                    </p>
                    <p className="text-emerald-500 font-semibold text-[9px]">
                      {fmt(totalWeight)} kg total
                    </p>
                  </div>
                )}
                {!hasProduction && !isSelected && (
                  <span className="text-[9px] text-gray-400 italic">
                    No production
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ✨ NEW: EXISTING PRODUCTION WARNING BANNER
// Shows when the selected DO item already has production record(s).
// Warns user this is an additional production run.
// ─────────────────────────────────────────────────────────────────────────────
const ExistingProductionWarning = ({ productions }) => {
  if (!productions || productions.length === 0) return null;

  return (
    <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs">
      <TriangleAlert size={16} className="text-amber-500 shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="font-black text-amber-800 text-xs mb-1.5">
          This DO Item Already Has {productions.length} Production Record(s)
        </p>
        <div className="space-y-1">
          {productions.map((p) => (
            <div
              key={p.id}
              className="flex items-center justify-between py-1 px-2 bg-white border border-amber-100 rounded-lg"
            >
              <div className="flex items-center gap-1.5">
                <FlaskConical size={10} className="text-amber-500" />
                <span className="font-bold text-amber-900">
                  {p.productionNumber}
                </span>
              </div>
              <div className="flex items-center gap-3 text-amber-700">
                <span className="flex items-center gap-0.5">
                  <Weight size={9} />
                  {fmt(p.totalActualWeight)} kg
                </span>
                <span className="text-amber-400">
                  {formatDate(p.productionDate)}
                </span>
              </div>
            </div>
          ))}
        </div>
        <p className="text-amber-600 mt-1.5 italic">
          Proceeding will create an additional production run for this item.
        </p>
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const AddProduction = ({ onClose, refreshData, productionData }) => {
  const currentUserId = useSelector(selectUserID);
  const isEditMode = !!productionData;

  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingDOs, setIsFetchingDOs] = useState(true);
  const [isFetchingStock, setIsFetchingStock] = useState(false);

  const [deliveryOrders, setDeliveryOrders] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [taggingGroups, setTaggingGroups] = useState([]);

  const [uomConversions, setUomConversions] = useState([]);
  const [productsMaster, setProductsMaster] = useState([]);
  const [warehouseStockMap, setWarehouseStockMap] = useState({});
  const [activeBom, setActiveBom] = useState(null);
  const [isFetchingBom, setIsFetchingBom] = useState(false);
  const [selectedItemToChange, setSelectedItemToChange] = useState(null);

  // Modal State for the Super Detail View
  const [detailModalTag, setDetailModalTag] = useState(null);

  // ✨ NEW: Existing productions for the selected DO
  const [existingProductions, setExistingProductions] = useState([]);
  const [isFetchingProductions, setIsFetchingProductions] = useState(false);

  const [formData, setFormData] = useState(() => ({
    warehouseId: productionData?.warehouseId
      ? String(productionData.warehouseId)
      : "",
    deliveryOrderId: productionData?.deliveryOrderId
      ? String(productionData.deliveryOrderId)
      : "",
    deliveryOrderItemId: productionData?.deliveryOrderItemId
      ? String(productionData.deliveryOrderItemId)
      : "",
    productionDate:
      productionData?.productionDate?.split("T")[0] ||
      new Date().toISOString().split("T")[0],
    remarks: productionData?.remarks || "",
  }));

  const [entries, setEntries] = useState({});

  // ─── 1. FETCH DEPENDENCIES ────────────────────────────────────────────────
  useEffect(() => {
    const fetchDependencies = async () => {
      setIsFetchingDOs(true);
      try {
        const [doRes, whRes, tagsRes, convRes, prodRes] = await Promise.all([
          axios.get(`${domain}/api/DeliveryOrders`),
          axios.get(`${domain}/api/Warehouses`),
          axios.get(`${domain}/api/ProductionTaggings/grouped`),
          axios.get(`${domain}/api/UomConversions`),
          axios.get(`${domain}/api/Products`),
        ]);

        const _deliveryOrders = (doRes.data || []).filter(
            d =>
                !d.isVoid &&
                d.deliveryOrderItems?.some(item => item.isBom)
        );

        setDeliveryOrders(_deliveryOrders);
        setWarehouses(whRes.data || []);
        setTaggingGroups(tagsRes.data || []);
        setUomConversions(convRes.data || []);
        setProductsMaster(prodRes.data || []);

        /* if (isEditMode && productionData.entries) {
          const prefilled = {};
          productionData.entries.forEach((e) => {
            if (prefilled[e.productionTaggingId]) {
              prefilled[e.productionTaggingId].quantity =
                parseFloat(prefilled[e.productionTaggingId].quantity) +
                parseFloat(e.quantity);
            } else {
              prefilled[e.productionTaggingId] = {
                quantity: e.quantity,
                productId: e.productId,
                uomId: e.uomId,
              };
            }
          });
          setEntries(prefilled);
        } */
      } catch {
        toast.error("Failed to load form dependencies.");
      } finally {
        setIsFetchingDOs(false);
      }
    };
    fetchDependencies();
  }, [isEditMode, productionData]);

  // ─── 2. FETCH WAREHOUSE STOCK MAP ─────────────────────────────────────────
  useEffect(() => {
    if (!formData.warehouseId) {
      setWarehouseStockMap({});
      return;
    }
    const fetchStock = async () => {
      setIsFetchingStock(true);
      try {
        const res = await axios.get(`${domain}/api/WarehouseStock/detail`);
        const warehouseData = res.data.find(
          (w) => String(w.warehouseId) === String(formData.warehouseId),
        );
        if (warehouseData?.stockItems) {
          const map = {};
          warehouseData.stockItems.forEach((item) => {
            map[item.productId] = {
              qty: item.currentQuantity,
              unit: item.uomName,
            };
          });
          setWarehouseStockMap(map);
        } else {
          setWarehouseStockMap({});
        }
      } catch {
        setWarehouseStockMap({});
      } finally {
        setIsFetchingStock(false);
      }
    };
    fetchStock();
  }, [formData.warehouseId]);

  // ─── 3. BOM FETCH ─────────────────────────────────────────────────────────
  const selectedDO = useMemo(
    () => deliveryOrders.find((d) => String(d.id) === formData.deliveryOrderId),
    [deliveryOrders, formData.deliveryOrderId],
  );

  const selectedDoItem = useMemo(() =>
    selectedDO?.deliveryOrderItems?.find(
      (i) => String(i.id) === formData.deliveryOrderItemId,
    ),
  [selectedDO, formData.deliveryOrderItemId],);

  // ─── ✨ NEW: FETCH EXISTING PRODUCTIONS WHEN DO CHANGES ─────────────────
  // This powers the DO Item Production Overview and the existing-production warning.
  useEffect(() => {
    if (!formData.deliveryOrderId) {
      setExistingProductions([]);
      return;
    }
    let isMounted = true;
    setIsFetchingProductions(true);
    axios
      .get(
        `${domain}/api/Productions/by-delivery-order/${formData.deliveryOrderId}`,
      )
      .then((res) => {
        if (isMounted) setExistingProductions(res.data || []);
      })
      .catch(() => {
        if (isMounted) setExistingProductions([]);
      })
      .finally(() => {
        if (isMounted) setIsFetchingProductions(false);
      });

    return () => {
      isMounted = false;
    };
  }, [formData.deliveryOrderId]);

  // ✨ NEW: Map of doItemId -> production records for that item
  const doItemProductionMap = useMemo(() => {
    const map = {};
    existingProductions.forEach((p) => {
      if (!map[p.deliveryOrderItemId]) map[p.deliveryOrderItemId] = [];
      map[p.deliveryOrderItemId].push(p);
    });
    return map;
  }, [existingProductions]);

  // ✨ NEW: Productions for the currently selected DO item
  const selectedItemProductions = useMemo(
    () =>
      formData.deliveryOrderItemId
        ? doItemProductionMap[parseInt(formData.deliveryOrderItemId)] || []
        : [],
    [doItemProductionMap, formData.deliveryOrderItemId],
  );

  // ─── UOM CONVERSION ENGINE ────────────────────────────────────────────────
  const getConversionRate = useCallback(
    (productId, fromUomId, baseUomId) => {
      if (!baseUomId || fromUomId === baseUomId) return 1;
      const direct = uomConversions.find(
        (c) =>
          c.productId === productId &&
          c.fromUomId === fromUomId &&
          c.toUomId === baseUomId,
      );
      if (direct) return direct.conversionRate;

      const reverse = uomConversions.find(
        (c) =>
          c.productId === productId &&
          c.fromUomId === baseUomId &&
          c.toUomId === fromUomId,
      );
      if (reverse && reverse.conversionRate > 0)
        return 1 / reverse.conversionRate;

      const step = uomConversions.find(
        (c) => c.productId === productId && c.fromUomId === fromUomId,
      );
      if (step && step.toUomId)
        return (
          step.conversionRate *
          getConversionRate(productId, step.toUomId, baseUomId)
        );

      return 1;
    },
    [uomConversions],
  );

  const [totalActualWeight, setTotalActualWeight] = useState(0);
  const [productionSummary, setProductionSummary] = useState([]);
  const [productBom, setProductBom] = useState(null);

  // ─── 4. WATERFALL ENGINE ──────────────────────────────────────────────────
  /* const waterfallResolutions = useMemo(() => {
    const resolutions = {};
    taggingGroups.forEach((group) => {
      group.tags.forEach((tag) => {
        const qty = parseFloat(entries[tag.id]?.quantity || 0);

        if (!tag.linkedProducts || tag.linkedProducts.length === 0) {
          resolutions[tag.id] = {
            splits: [],
            remaining: 0,
            isFullyResolved: true,
            totalAvailable: 0,
            requested: qty,
          };
          return;
        }

        const splits = [];
        let remainingTagQty = qty;
        let totalAvailableInTagUom = 0;

        for (const lp of tag.linkedProducts) {
          const product = productsMaster.find((p) => p.id === lp.productId);
          const baseUomId = product?.baseUomId || null;

          const rateToBase = getConversionRate(
            lp.productId,
            lp.uomId,
            baseUomId,
          );
          const availableBase = warehouseStockMap[lp.productId]?.qty || 0;
          const baseUom = warehouseStockMap[lp.productId]?.unit || "units";

          const availableInTagUom =
            rateToBase > 0 ? availableBase / rateToBase : 0;
          totalAvailableInTagUom += availableInTagUom;

          const neededBase = remainingTagQty * rateToBase;
          const baseToUse =
            remainingTagQty > 0 ? Math.min(availableBase, neededBase) : 0;
          const qtyToUseInLinkedUom =
            rateToBase > 0 ? baseToUse / rateToBase : 0;

          splits.push({
            productId: lp.productId,
            productName: lp.productName,
            uomId: lp.uomId,
            baseUom,
            rateToBase,
            availableBase,
            quantity: qtyToUseInLinkedUom,
            consumedBase: baseToUse,
            priority: lp.priority,
          });

          remainingTagQty -= qtyToUseInLinkedUom;
        }

        resolutions[tag.id] = {
          splits,
          remaining: remainingTagQty,
          isFullyResolved: remainingTagQty <= 0,
          totalAvailable: totalAvailableInTagUom,
          requested: qty,
        };
      });
    });
    return resolutions;
  }, [
    taggingGroups,
    entries,
    warehouseStockMap,
    productsMaster,
    getConversionRate,
  ]); */

  const waterfallResolutions = useMemo(() => {
    const resolutions = {};
    productBom?.components.forEach((tag) => {
      const qty = parseFloat(entries[tag.componentProductId]?.quantity || 0);

      const splits = [];
      let remainingTagQty = qty;
      let totalAvailableInTagUom = 0;

      const product = productsMaster.find((p) => p.id === tag.componentProductId);
      const baseUomId = product?.baseUomId || null;

      const rateToBase = getConversionRate(
        tag.componentProductId,
        tag.uomId,
        baseUomId,
      );
      const availableBase = warehouseStockMap[tag.componentProductId]?.qty || 0;
      const baseUom = warehouseStockMap[tag.componentProductId]?.unit || "units";

      const availableInTagUom =
        rateToBase > 0 ? availableBase / rateToBase : 0;
      totalAvailableInTagUom += availableInTagUom;

      const neededBase = remainingTagQty * rateToBase;
      const baseToUse =
        remainingTagQty > 0 ? Math.min(availableBase, neededBase) : 0;
      const qtyToUseInLinkedUom =
        rateToBase > 0 ? baseToUse / rateToBase : 0;

      splits.push({
        productId: tag.componentProductId,
        productName: tag.componentProductName,
        uomId: tag.uomId,
        baseUom,
        rateToBase,
        availableBase,
        quantity: qtyToUseInLinkedUom,
        consumedBase: baseToUse,
        priority: true,
      });

      remainingTagQty -= qtyToUseInLinkedUom;

      // for edit isEditMode
      const editEntries = productionData?.entries?.[tag.componentProductId]

      resolutions[tag.componentProductId] = {
        splits,
        remaining: remainingTagQty,
        isFullyResolved: remainingTagQty <= 0,
        totalAvailable: totalAvailableInTagUom + (editEntries ? parseFloat(editEntries?.quantity ?? 0) : 0),
        requested: qty,
      };
    });
    return resolutions;
  }, [
    productBom,
    entries,
    warehouseStockMap,
    productsMaster,
    getConversionRate,
    productionData,
  ]);

  useEffect(() => {
    const hasValidQuantity = Object.values(entries).some((entry) => {
      const qty = parseFloat(entry.quantity);
      return !isNaN(qty) && qty > 0;
    });

    if (productBom?.components && hasValidQuantity) {
      // 1. Calculate possible production for each component
      const productionValues = productBom.components.map((comp) => {
        const entry = entries[comp.componentProductId];
        const available = parseFloat(entry?.quantity || 0);
        const requiredPerUnit = comp.quantity;

        if (requiredPerUnit <= 0) return Infinity;
        return available / requiredPerUnit;
      });

      // 2. Get the bottleneck and ROUND DOWN
      // Math.floor(10.6) becomes 10
      const finalProduction = Math.floor(Math.min(...productionValues));
      setTotalActualWeight(finalProduction);

      // 3. Calculate excess for each product
      const excessReport = productBom.components.map((comp) => {
        const entry = entries[comp.componentProductId];
        const waterfallResolution = waterfallResolutions[comp.componentProductId];
        const available = parseFloat(entry?.quantity || 0);
        const requiredTotal = comp.quantity * finalProduction;
        
        const excess = available - requiredTotal;

        return {
          productName: comp.componentProductName,
          productId: comp.componentProductId,
          used: requiredTotal,
          available: available,
          excess: excess, // This is the leftover
          totalStock: waterfallResolution?.totalAvailable ?? 0
        };
      });

      setProductionSummary(excessReport)
    }
  }, [productBom, entries, waterfallResolutions]);

  const fetchProductBomByProductId = useCallback(async (_data) => {
    try {
      const response = await axios.get(`${domain}/api/ProductBom/productId/${_data.productId}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      return (response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
      return null
    }
  }, []);

  useEffect(() => {
    if (selectedDoItem && !doItemProductionMap[selectedDoItem.id]?.length > 0) {
      if (!selectedDoItem?.productId) return setActiveBom(null);
      let isMounted = true;
      const fetchBom = async () => {
        setIsFetchingBom(true);
        try {
          const res = await axios.get(
            `${domain}/api/ProductBom/active?bomProductId=${selectedDoItem.productId}`,
          );
          if (isMounted) {
            setActiveBom(res.data)
          };
        } catch {
          if (isMounted) setActiveBom(null);
        } finally {
          if (isMounted) setIsFetchingBom(false);
        }
      };
      fetchBom();
      return () => {
        isMounted = false;
      };
    };
  }, [selectedDoItem, doItemProductionMap]);

  useEffect(() => {
    if (selectedDoItem && !doItemProductionMap[selectedDoItem.id]?.length > 0) {
      const fetch = async () => {
        setIsLoading(true);
        const _productBom = await fetchProductBomByProductId(selectedDoItem);

        setProductBom(_productBom);
        setIsLoading(false);
      }

      fetch();
    }
  }, [selectedDoItem, fetchProductBomByProductId, doItemProductionMap])

  // ─── 5. TOTAL ACTUAL WEIGHT ───────────────────────────────────────────────
  /* const totalActualWeight = useMemo(() => {
    let total = 0;
    taggingGroups.forEach((group) =>
      group.tags.forEach((tag) => {
        if (tag.unit.toLowerCase() === "kg" || tag.unit.toLowerCase() === "g")
          total += parseFloat(entries[tag.id]?.quantity || 0);
      }),
    );
    return total;
  }, [entries, taggingGroups]); */

  const handleEntryChange = (tag, field, value) => {
    if (
      field === "quantity" &&
      value !== "" &&
      (isNaN(value) || parseFloat(value) < 0)
    )
      return;

    setEntries((prev) => ({
      ...prev,
      [tag.id]: { ...prev[tag.id], [field]: value },
    }));
  };

  const handleEntryChangeProduct = (tag, field, value) => {
    if (
      field === "quantity" &&
      value !== "" &&
      (isNaN(value) || parseFloat(value) < 0)
    )
      return;

    setEntries((prev) => ({
      ...prev,
      [tag.componentProductId]: { ...prev[tag.componentProductId], [field]: value },
    }));
  };

  const handleResetAll = () => {
    setEntries({});
    setProductionSummary([])
    setTotalActualWeight(0)
  };

  const handleFillToTarget = async () => {
    setIsLoading(true);
    try {
        //const productBom = await fetchProductBomByProductId(selectedDoItem);
        
        // 1. Get the Production Multiplier (How many 'Combo A' are we making?)
        const productionMultiplier = (
            selectedDoItem?.quantityDelivered ?? 
            selectedDoItem?.quantity ?? 
            selectedDoItem?.quantityReceived ?? 
            0
        );

        const newEntries = { ...entries };

        productBom?.components?.forEach((group) => {
          const totalCalculatedQty = group.quantity * productionMultiplier;

          newEntries[group.componentProductId] = {
              ...newEntries[group.componentProductId],
              quantity: totalCalculatedQty.toString(),
          };
        });
        
        setEntries(newEntries);
    } catch (error) {
        console.error("Failed to fetch BOM or fill target:", error);
    } finally {
        setIsLoading(false);
    }
  };

  // ─── 6. SAVE / SUBMIT ─────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!formData.warehouseId)
      return toast.error("Please select a Source Warehouse.");
    if (!formData.deliveryOrderId)
      return toast.error("Please select a Delivery Order.");
    if (!formData.deliveryOrderItemId)
      return toast.error("Please select a Target Product.");
    if (!formData.productionDate)
      return toast.error("Production date is required.");

    const formattedEntries = [];

    /* for (const group of taggingGroups) {
      for (const tag of group.tags) {
        const qty = parseFloat(entries[tag.componentProductId]?.quantity || 0);
        if (qty <= 0) continue;
        if (!tag.linkedProducts || tag.linkedProducts.length === 0) continue;

        const resolution = waterfallResolutions[tag.componentProductId];
        if (!resolution || !resolution.isFullyResolved) {
          return toast.error(
            `Insufficient stock for "${tag.tagName}". Need ${fmt(resolution?.remaining || 0)} more ${tag.unit} across priority products.`,
          );
        }

        for (const split of resolution.splits) {
          if (split.quantity <= 0) continue;
          formattedEntries.push({
            productionTaggingId: tag.componentProductId,
            productId: split.productId,
            uomId: split.uomId || 0,
            quantity: split.quantity,
          });
        }
      }
    } */

    for (const [productId, entryValue] of Object.entries(entries)) {
      const qty = parseFloat(entryValue.quantity) || 0;

      const foundData = productBom?.components?.find(i => i.componentProductId?.toString() === productId?.toString());
      
      if (qty > 0) {
          formattedEntries.push({
            productionTaggingId: productId,
            productId: productId,
            uomId: foundData?.uomId || 0,
            quantity: qty,
          });
      }
    }

    if (formattedEntries.length === 0)
      return toast.error("Please enter at least one material quantity.");

    setIsLoading(true);
    const payload = {
      userID: currentUserId,
      warehouseId: parseInt(formData.warehouseId),
      deliveryOrderId: parseInt(formData.deliveryOrderId),
      deliveryOrderNumber: selectedDO.deliveryOrderNumber,
      deliveryOrderItemId: parseInt(formData.deliveryOrderItemId),
      productionDate: formData.productionDate,
      remarks: formData.remarks,
      totalActualWeight,
      entries: formattedEntries,
    };

    try {
      if (isEditMode) {
        await axios.put(
          `${domain}/api/Productions/${productionData.id}`,
          payload,
        );
        toast.success("Production updated successfully.");
      } else {
        await axios.post(`${domain}/api/Productions`, payload);
        toast.success("Production created successfully.");
      }
      refreshData();
      onClose();
    } catch (err) {
      console.error('There was a problem adding data:', err);
      toast.error(err.response?.data?.message || "Failed to save.");
    } finally {
      setIsLoading(false);
    }
  };

  const stockWarnings = productionSummary?.filter(
    (item) => item.available > item.totalStock
  );


  const [showChangeItemForm, setShowChangeItemForm] = useState(false)
  const openChangeItemForm = (_item) => {
    setShowChangeItemForm(true);
    setSelectedItemToChange(_item)
  }
  const closeChangeItemForm = () => {
    setShowChangeItemForm(false);
    setSelectedItemToChange(null)
  }

  useEffect(() => {
    if(selectedDoItem && productionData){
      setEntries(productionData.entries)
      const reversedEntries = Object.values(productionData.entries).reverse();
      setActiveBom(prev => ({
        ...prev,
        components: Object.values(reversedEntries).map(component => ({
          ...component,
          componentItemCode: component.productItemCode ?? null,
          componentProductId: component.productId ?? null,
          componentProductName: component.productName ?? null,
          componentProductBrandName: component.brandName ?? null,
        })),
      }));
      setProductBom(prev => ({
        ...prev,
        components: Object.values(reversedEntries).map(component => ({
          ...component,
          componentItemCode: component.productItemCode ?? null,
          componentProductId: component.productId ?? null,
          componentProductName: component.productName ?? null,
          componentProductBrandName: component.brandName ?? null,
        })),
      }));
    }
  }, [selectedDoItem, productionData])

  console.log({selectedDoItem})

  // ─── RENDER ───────────────────────────────────────────────────────────────
  return (
    <>
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-900/70 backdrop-blur-sm p-4 lg:p-8">
        <div className="flex h-full max-h-[95vh] w-full max-w-[1400px] flex-col rounded-2xl bg-white shadow-2xl overflow-hidden border border-gray-200">
          {/* HEADER */}
          <div className="flex items-center justify-between border-b border-gray-100 bg-white px-6 py-4 shrink-0">
            <div className="flex items-center gap-4">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-xl text-white shadow-lg ${isEditMode ? "bg-amber-500 shadow-amber-200" : "bg-emerald-600 shadow-emerald-200"}`}
              >
                <FaFlask size={17} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900 leading-none">
                  {isEditMode
                    ? "Edit Production Record"
                    : "New Production Record"}
                </h2>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">
                  Dynamic Material Consumption
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-red-500 transition-colors bg-gray-50 hover:bg-red-50 rounded-lg"
            >
              <FaTimes size={18} />
            </button>
          </div>

          {/* TWO-COLUMN BODY */}
          <div className="flex-grow overflow-y-auto bg-gray-50/50 p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-full">
              {/* LEFT COLUMN: Configuration */}
              <div className="space-y-5 flex flex-col">
                <div className="rounded-xl border border-indigo-100 bg-white p-5 shadow-sm space-y-4 shadow-indigo-50/50">
                  <h3 className="text-xs font-black uppercase tracking-widest text-indigo-500 flex items-center gap-2">
                    <PackageCheck size={14} /> 1. Configuration
                  </h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label className={labelClass}>
                        Warehouse Source <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Warehouse
                          size={13}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        />
                        <select
                          value={formData.warehouseId}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              warehouseId: e.target.value,
                            }))
                          }
                          className={`${inputClass} pl-8 appearance-none border-indigo-200 focus:border-indigo-500`}
                        >
                          <option value="">— Select Warehouse Source —</option>
                          {warehouses.map((w) => (
                            <option key={w.id} value={w.id}>
                              {w.warehouseName}
                            </option>
                          ))}
                        </select>
                        <ChevronDown
                          size={13}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                        />
                      </div>
                      {formData.warehouseId && (
                        <div className="mt-1 flex items-center gap-1.5 text-[10px] font-semibold">
                          {isFetchingStock ? (
                            <>
                              <RefreshCw
                                size={10}
                                className="animate-spin text-indigo-400"
                              />
                              <span className="text-indigo-400">
                                Loading stock snapshot…
                              </span>
                            </>
                          ) : (
                            <>
                              <CheckCircle2
                                size={10}
                                className="text-emerald-500"
                              />
                              <span className="text-emerald-600">
                                Stock loaded —{" "}
                                {Object.keys(warehouseStockMap).length} products
                                tracked
                              </span>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="sm:col-span-1">
                      <label className={labelClass}>
                        Delivery Order <span className="text-red-500">*</span>
                      </label>
                      {isFetchingDOs ? (
                        <div className="flex items-center gap-2 h-[38px] px-3 border border-gray-200 rounded-lg bg-gray-50">
                          <Loader2
                            size={14}
                            className="animate-spin text-gray-400"
                          />
                          <span className="text-sm text-gray-400">
                            Loading DOs…
                          </span>
                        </div>
                      ) : (
                        <div className="relative">
                          <PackageCheck
                            size={13}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                          />
                          <select
                            value={formData.deliveryOrderId}
                            onChange={(e) =>
                              setFormData((p) => ({
                                ...p,
                                deliveryOrderId: e.target.value,
                                deliveryOrderItemId: "",
                              }))
                            }
                            className={`${inputClass} pl-8 appearance-none border-indigo-200 focus:border-indigo-500`}
                          >
                            <option value="">— Select Delivery Order —</option>
                            {deliveryOrders.map((d) => {
                              return (
                                <option key={d.id} value={String(d.id)}>
                                  {`${d.deliveryOrderNumber} | ${d.customerName}` }
                                </option>
                              )
                            })}
                          </select>
                          <ChevronDown
                            size={13}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                          />
                        </div>
                      )}
                    </div>
                    <div>
                      <label className={labelClass}>
                        Production Date <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Calendar
                          size={13}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        />
                        <input
                          type="date"
                          value={formData.productionDate}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              productionDate: e.target.value,
                            }))
                          }
                          className={`${inputClass} pl-8 border-indigo-200 focus:border-indigo-500`}
                        />
                      </div>
                    </div>

                    {/* ✨ NEW: Target Product dropdown now annotates items with production status */}
                    {selectedDO && (
                      <div className="sm:col-span-2">
                        <label className={labelClass}>
                          Target Product <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <Layers
                            size={13}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                          />
                          <select
                            value={formData.deliveryOrderItemId}
                            onChange={(e) =>
                              setFormData((p) => ({
                                ...p,
                                deliveryOrderItemId: e.target.value,
                              }))
                            }
                            className={`${inputClass} pl-8 appearance-none border-indigo-200 focus:border-indigo-500`}
                          >
                            <option value="">— Select Product from DO —</option>
                            {selectedDO.deliveryOrderItems?.filter(i => i.isBom).map((item) => {
                              // ✨ Annotate with production status
                              const itemProds =
                                doItemProductionMap[item.id] || [];
                              const prodLabel =
                                itemProds.length > 0
                                  ? ` ✓ ${itemProds.length} produced`
                                  : "";
                              return (
                                <option key={item.id} value={String(item.id)}>
                                  {item.productName} (Qty:{" "}
                                  {item.quantityDelivered ?? item.quantity ?? item.quantityReceived})
                                  {prodLabel}
                                </option>
                              );
                            })}
                          </select>
                          <ChevronDown
                            size={13}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                          />
                        </div>

                        {/* ✨ NEW: Warning if selected item already has productions */}
                        {!isEditMode && selectedItemProductions.length > 0 && (
                          <div className="mt-2">
                            <ExistingProductionWarning
                              productions={selectedItemProductions}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {selectedDO && (
                    <div className="flex flex-wrap items-center gap-3 p-3 bg-indigo-50 border border-indigo-100 rounded-lg mt-2">
                      <div className="flex items-center gap-1.5 text-xs text-indigo-700 font-semibold">
                        <Hash size={12} /> {selectedDO.deliveryOrderNumber}
                      </div>
                      <span className="text-indigo-200">|</span>
                      <div className="flex items-center gap-1.5 text-xs text-indigo-600">
                        <FileText size={12} /> Ref SO:{" "}
                        {selectedDO.salesOrders?.map((i, index) => 
                          <span key={index}>
                            {i.salesOrderNumber || "N/A"}
                          </span>
                        )}
                      </div>
                      {selectedDO.locationName && (
                        <>
                          <span className="text-indigo-200">|</span>
                          <div className="flex items-center gap-1 text-xs text-indigo-500">
                            <MapPin size={11} /> {selectedDO.locationName}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* ✨ NEW: DO Items Production Overview */}
                {selectedDO && (
                  <DOItemsProductionOverview
                    selectedDO={selectedDO}
                    doItemProductionMap={doItemProductionMap}
                    selectedItemId={formData.deliveryOrderItemId}
                    isFetchingProductions={isFetchingProductions}
                  />
                )}

                {/* BOM Reference Card */}
                <div className="rounded-xl border border-gray-200 bg-white flex-grow flex flex-col shadow-sm overflow-hidden">
                  <div className="p-4 border-b border-gray-100 bg-gray-50/50">
                    <h3 className="text-xs font-black uppercase tracking-widest text-gray-500 flex items-center gap-2">
                      <Layers size={14} /> Target Ingredients (BOM Multiplier)
                    </h3>
                  </div>
                  <div className="p-4 flex-grow overflow-y-auto">
                    {!selectedDO ? (
                      <div className="h-full flex flex-col items-center justify-center text-gray-400 space-y-2 py-10">
                        <PackageCheck size={32} className="opacity-20" />
                        <p className="text-sm">
                          Select a Delivery Order to begin.
                        </p>
                      </div>
                    ) : !selectedDoItem ? (
                      <div className="h-full flex flex-col items-center justify-center text-gray-400 space-y-2 py-10">
                        <Layers size={32} className="opacity-20" />
                        <p className="text-sm">
                          Select a Product above to view target ingredients.
                        </p>
                        {/* ✨ NEW: hint about multiple items */}
                        {(selectedDO.deliveryOrderItems || []).length > 1 && (
                          <p className="text-xs text-indigo-400 font-semibold bg-indigo-50 border border-indigo-100 px-3 py-1.5 rounded-lg">
                            This DO has {selectedDO.deliveryOrderItems.length}{" "}
                            items — each has its own BOM. Create one production
                            per item.
                          </p>
                        )}
                      </div>
                    ) : isFetchingBom ? (
                      <div className="h-full flex flex-col items-center justify-center text-indigo-400 space-y-2 py-10">
                        <Loader2
                          size={32}
                          className="animate-spin opacity-50"
                        />
                        <p className="text-sm font-semibold">
                          Calculating Recipe Target…
                        </p>
                      </div>
                    ) : activeBom?.components?.length > 0 ? (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-indigo-600 rounded-xl text-white shadow-md shadow-indigo-200">
                          <div className="flex items-center gap-3">
                            <div className="p-2.5 bg-white/20 rounded-lg backdrop-blur-sm">
                              <Calculator size={18} className="text-white" />
                            </div>
                            <div>
                              <p className="text-[10px] font-black uppercase tracking-widest text-indigo-200 mb-0.5">
                                Target Production
                              </p>
                              <p className="text-base font-bold leading-tight">
                                {selectedDoItem.productName}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-3xl font-black leading-none">
                              {selectedDoItem.quantityReceived ??
                                selectedDoItem.quantity}
                            </p>
                            <p className="text-[10px] font-black uppercase tracking-widest text-indigo-200 mt-0.5">
                              Orders to Produce
                            </p>
                          </div>
                        </div>
                        <div className="border border-indigo-100 rounded-xl overflow-hidden bg-white shadow-sm">
                          <div className="grid grid-cols-[1fr_auto_auto_auto_auto] items-center gap-3 px-4 py-2.5 bg-indigo-50/50 border-b border-indigo-100 text-[10px] font-black text-indigo-500 uppercase tracking-wider">
                            <div>Ingredient</div>
                            <div className="w-[75px] text-right">Base</div>
                            <div className="w-[50px] text-center">Multi</div>
                            <div className="w-[10px]"></div>
                            <div className="w-[85px] text-right">Target</div>
                          </div>
                          <div className="divide-y divide-gray-50">
                            {activeBom.components.map((comp) => {
                              const multiplier =
                                parseFloat(
                                  selectedDoItem.quantityReceived ??
                                    selectedDoItem.quantity,
                                ) || 1;
                              const targetQty = comp.quantity * multiplier;
                              return (
                                <div
                                  key={comp.id}
                                  className="grid grid-cols-[1fr_auto_auto_auto_auto] items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
                                >
                                  <div className="flex flex-row gap-2">
                                    <button
                                      className="flex items-center justify-center p-2 rounded-md bg-blue-500 hover:bg-blue-600 text-white transition-colors duration-200"
                                      title="Change Item"
                                      onClick={()=>openChangeItemForm(comp)}
                                    >
                                      <Pencil size={18} />
                                    </button>
                                    <div className="flex flex-col overflow-hidden">
                                      <span className="text-sm font-bold text-gray-800 truncate">
                                        {`${comp.componentProductName} ${comp.componentProductBrandName ? `(${comp.componentProductBrandName})` : ""}`}
                                      </span>
                                      <span className="text-[9px] font-bold text-gray-400 tracking-wider uppercase mt-0.5">
                                        {comp.componentItemCode || "N/A"}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="w-[75px] text-right">
                                    <p className="text-xs font-semibold text-gray-500">
                                      {fmt(comp.quantity)}
                                    </p>
                                    <p className="text-[9px] text-gray-400 font-bold uppercase">
                                      {comp.uomName || "Units"}
                                    </p>
                                  </div>
                                  <div className="w-[50px] text-center">
                                    <span className="text-xs font-black text-indigo-300">
                                      × {multiplier}
                                    </span>
                                  </div>
                                  <div className="w-[10px] text-center">
                                    <span className="text-xs font-black text-gray-300">
                                      =
                                    </span>
                                  </div>
                                  <div className="w-[85px] text-right">
                                    <p className="text-sm font-black text-indigo-600">
                                      {fmt(targetQty)}
                                    </p>
                                    <p className="text-[9px] text-indigo-400 font-bold uppercase">
                                      {comp.uomName || "Units"}
                                    </p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 p-4 bg-red-50 text-red-600 text-sm rounded-xl border border-red-100 shadow-sm">
                        <AlertCircle size={20} className="shrink-0" />
                        <div>
                          <p className="font-bold">No BOM Recipe Found.</p>
                          <p className="text-xs text-red-400 mt-0.5 leading-snug">
                            This product might be a regular item or missing an
                            active BOM configuration.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* RIGHT COLUMN: Dynamic Material Entry */}
              <div
                className={`space-y-5 transition-opacity duration-300 ${!selectedDoItem ? "opacity-40 pointer-events-none grayscale-[50%]" : "opacity-100"}`}
              >
                {selectedDoItem && formData.warehouseId && isFetchingStock && (
                  <div className="flex items-center gap-3 p-3 bg-indigo-50 border border-indigo-100 rounded-xl text-sm text-indigo-700">
                    <RefreshCw size={14} className="animate-spin shrink-0" />
                    <span className="font-semibold">
                      Loading warehouse stock for waterfall calculation…
                    </span>
                  </div>
                )}

                {/* Target */}
                <div
                  className={`rounded-xl border-2 p-5 shadow-sm transition-all duration-500 ${
                    (selectedDoItem?.quantityDelivered ?? selectedDoItem?.quantity ?? selectedDoItem?.quantityReceived ?? 0) > 0 
                      ? "border-yellow-300 bg-yellow-50 scale-[1.01]" 
                      : "border-gray-200 bg-white"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Icon Container */}
                      <div
                        className={`p-3 rounded-xl transition-colors ${
                          (selectedDoItem?.quantityDelivered ?? selectedDoItem?.quantity ?? selectedDoItem?.quantityReceived ?? 0) > 0 ? "bg-yellow-100" : "bg-gray-100"
                        }`}
                      >
                        <Target
                          size={20}
                          className={
                            (selectedDoItem?.quantityDelivered ?? selectedDoItem?.quantity ?? selectedDoItem?.quantityReceived ?? 0) > 0
                              ? "text-yellow-600"
                              : "text-gray-400"
                          }
                        />
                      </div>

                      {/* Label Content */}
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">
                          Target to Produce
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {selectedDoItem?.productName || "No product selected"}
                        </p>
                      </div>
                    </div>

                    {/* Big Value Display */}
                    <div className="text-right">
                      <p className={`text-3xl font-black tabular-nums ${
                        (selectedDoItem?.quantityDelivered ?? selectedDoItem?.quantity ?? selectedDoItem?.quantityReceived ?? 0) > 0 ? "text-yellow-700" : "text-gray-300"
                      }`}>
                        {(selectedDoItem?.quantityDelivered ?? selectedDoItem?.quantity ?? selectedDoItem?.quantityReceived ?? 0).toLocaleString()}
                        <span className="ml-1 text-sm font-bold uppercase tracking-tighter">{selectedDoItem?.unitName ?? ""}</span>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons Group */}
                <div className="flex items-center gap-3 mb-6">
                  {/* Reset Button */}
                  <button
                    onClick={() => handleResetAll()} // Make sure to define this function to clear your 'entries' state
                    className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-gray-500 shadow-sm transition-all hover:bg-gray-50 active:scale-95"
                  >
                    <RotateCcw size={14} />
                    Reset
                  </button>

                  {/* Fill to Target Button */}
                  <button
                    onClick={() => handleFillToTarget()} // Make sure to define this function to set entries to BOM values
                    className="flex flex-[2] items-center justify-center gap-2 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-indigo-600 shadow-sm transition-all hover:bg-indigo-100 active:scale-95"
                  >
                    <Zap size={14} className="fill-current" />
                    Fill to Target
                  </button>
                </div>

                <div className="hidden">
                  {/* Dynamic Tagging Groups */}
                  {taggingGroups.map((group) => (
                    <div
                      key={group.groupName}
                      className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm space-y-4"
                    >
                      <h3 className="text-xs font-black uppercase tracking-widest text-gray-800 flex items-center gap-2">
                        <Layers size={14} className="text-indigo-400" />{" "}
                        {group.groupName}
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {group.tags.map((tag) => {
                          const val = entries[tag.componentProductId]?.quantity || "";
                          const c = getColorClasses(tag.color);
                          const resolution = waterfallResolutions[tag.componentProductId];

                          return (
                            <div key={tag.componentProductId} className="space-y-1.5">
                              <label className={labelClass}>{tag.tagName}</label>
                              <div className="flex flex-col">
                                <div className="relative group">
                                  <Tag
                                    size={15}
                                    className={`absolute left-3 top-1/2 -translate-y-1/2 ${c.text}`}
                                  />
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={val}
                                    onChange={(e) =>
                                      handleEntryChange(
                                        tag,
                                        "quantity",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="0.00"
                                    className={`block w-full rounded-xl pl-9 pr-10 py-2.5 text-sm font-bold text-gray-900 outline-none transition-all bg-gray-50 focus:bg-white border ${c.border} ${c.ring}`}
                                  />
                                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-black text-gray-400 uppercase">
                                    {tag.unit}
                                  </span>
                                </div>

                                <ConsumptionSummary
                                  tag={tag}
                                  resolution={resolution}
                                  onClick={() =>
                                    setDetailModalTag({ tag, resolution })
                                  }
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  {/* Mix Composition Breakdown */}
                  {totalActualWeight > 0 && (
                    <div className="p-5 bg-gray-50 rounded-xl border border-gray-100 space-y-3">
                      <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">
                        Mix Composition Breakdown
                      </p>
                      {taggingGroups
                        .flatMap((g) => g.tags)
                        .filter(
                          (t) => (parseFloat(entries[t.id]?.quantity) || 0) > 0,
                        )
                        .map((tag) => (
                          <WeightBar
                            key={tag.componentProductId}
                            label={tag.tagName}
                            value={parseFloat(entries[tag.componentProductId]?.quantity) || 0}
                            total={totalActualWeight}
                            barColor={getColorClasses(tag.color).bar}
                          />
                        ))}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {(productBom?.components || [])?.map((group) => {
                    const resolution = waterfallResolutions[group.componentProductId];
                    return (
                      <div
                        key={group.id}
                        className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm space-y-4"
                      >
                        <h3 className="text-xs font-black uppercase tracking-widest text-gray-800 flex items-center gap-2">
                          <Layers size={14} className="text-indigo-400" />{" "}
                          {`${group.componentProductName} ${group.componentProductBrandName ? `(${group.componentProductBrandName})` : ""}`}
                        </h3>
                          <div className="space-y-1.5">
                            <div className="flex flex-col">
                              <div className="relative group">
                                <Tag
                                  size={15}
                                  className={`absolute left-3 top-1/2 -translate-y-1/2`}
                                />
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={entries[group.componentProductId]?.quantity || ""} 
                                  onChange={(e) =>
                                    handleEntryChangeProduct(
                                      group,
                                      "quantity",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="0.00"
                                  className={`block w-full rounded-xl pl-9 pr-10 py-2.5 text-sm font-bold text-gray-900 outline-none transition-all bg-gray-50 focus:bg-white border`}
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-black text-gray-400 uppercase">
                                  {group.uomCode}
                                </span>
                              </div>

                              {<ConsumptionSummaryProduct
                                tag={group}
                                resolution={resolution}
                                value={entries[group.componentProductId]?.quantity}
                                onClick={() =>
                                  setDetailModalTag({ group,resolution })
                                }
                              />}
                            </div>
                          </div>
                      </div>
                    )})}
                </div>

                {/* Stock Warning Note */}
                {stockWarnings.length > 0 && (
                  <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 rounded-full bg-red-100 p-1">
                        <AlertCircle size={16} className="text-red-600" />
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-red-800">
                          Insufficient Physical Stock
                        </h4>
                        <p className="text-xs text-red-600 mt-1">
                          The following items have input quantities exceeding current warehouse stock:
                        </p>
                        <ul className="mt-2 space-y-1">
                          {stockWarnings.map((item) => (
                            <li key={item.productId} className="flex items-center gap-2 text-[11px] font-bold text-red-700">
                              <span className="h-1 w-1 rounded-full bg-red-400" />
                              {item.productName}: Input ({fmt(item.available)}) &gt; Stock ({fmt(item.totalStock)})
                              <span className="ml-1 font-black uppercase text-[9px] bg-red-200 px-1.5 py-0.5 rounded">
                                Short {fmt(item.available - item.totalStock)} {item.uom}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Production Summary Breakdown */}
                {productionSummary?.length > 0 && totalActualWeight > 0 && (
                  <div className="mt-4 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                    <div className="bg-gray-50 px-5 py-3 border-b border-gray-200">
                      <h3 className="text-[10px] font-black uppercase tracking-widest text-gray-500 flex items-center gap-2">
                        <Layers size={14} /> Material Usage Breakdown
                      </h3>
                    </div>
                    
                    <div className="divide-y divide-gray-100">
                      {productionSummary?.map((item) => (
                        <div key={item.productId} className="px-5 py-4 flex items-center justify-between">
                          <div>
                            <p className="text-sm font-bold text-gray-900">{item.productName}</p>
                            <p className="text-[10px] font-medium text-gray-400 uppercase tracking-tight">
                              Total Input: {fmt(item.available)} {item.uom}
                            </p>
                          </div>
                          
                          <div className="flex gap-8 text-right">
                            <div>
                              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Used</p>
                              <p className="text-sm font-bold text-indigo-600">
                                -{fmt(item.used)} <span className="text-[10px]">{item.uom}</span>
                              </p>
                            </div>

                            <div>
                              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Excess</p>
                              <p className={`text-sm font-bold ${item.excess > 0 ? "text-amber-600" : "text-gray-400"}`}>
                                {item.excess > 0 ? `+${fmt(item.excess)}` : "0.00"} <span className="text-[10px]">{item.uom}</span>
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="bg-emerald-50/50 px-5 py-3 border-t border-emerald-100">
                      <p className="text-[10px] font-medium text-emerald-700 italic">
                        * Calculation based on recipe requirements for {totalActualWeight} units. 
                        Any excess represents items not included in this production run.
                      </p>
                    </div>
                  </div>
                )}

                {/* Total Actual Weight */}
                <div
                  className={`rounded-xl border-2 p-5 shadow-sm transition-all duration-500 ${totalActualWeight > 0 ? "border-emerald-300 bg-emerald-50 scale-[1.01]" : "border-gray-200 bg-white"}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className={`p-3 rounded-xl transition-colors ${totalActualWeight > 0 ? "bg-emerald-100" : "bg-gray-100"}`}
                      >
                        <Calculator
                          size={20}
                          className={
                            totalActualWeight > 0
                              ? "text-emerald-600"
                              : "text-gray-400"
                          }
                        />
                      </div>
                      <div>
                        <p className="hidden text-[10px] font-black uppercase tracking-widest text-gray-500">
                          Total Actual Weight
                        </p>
                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">
                          {`Total Actual ${selectedDoItem?.productName ?? ""}`}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          Sum of configured material items
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p
                        className={`text-3xl font-black tabular-nums transition-all ${totalActualWeight > 0 ? "text-emerald-700" : "text-gray-300"}`}
                      >
                        {fmt(totalActualWeight)}
                      </p>
                      <p
                        className={`hidden text-xs font-bold ${totalActualWeight > 0 ? "text-emerald-500" : "text-gray-300"}`}
                      >
                        kilograms
                      </p>
                    </div>
                  </div>
                </div>

                {/* Remarks */}
                <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                  <label className={labelClass}>Remarks / Notes</label>
                  <textarea
                    rows={2}
                    value={formData.remarks}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, remarks: e.target.value }))
                    }
                    className={inputClass}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div className="flex items-center justify-between border-t border-gray-100 bg-white px-6 py-4 shrink-0">
            <button
              onClick={onClose}
              className="text-sm font-bold text-gray-400 hover:text-gray-600 transition-colors px-4 py-2"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading || !selectedDoItem || isFetchingStock || stockWarnings.length > 0}
              className={`flex items-center gap-2 rounded-xl px-8 py-3 text-sm font-black text-white shadow-lg transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ${isEditMode ? "bg-amber-500 shadow-amber-100 hover:bg-amber-600" : "bg-emerald-600 shadow-emerald-100 hover:bg-emerald-700"}`}
            >
              {isLoading ? (
                <Loader2 className="animate-spin" size={16} />
              ) : isFetchingStock ? (
                <RefreshCw className="animate-spin" size={16} />
              ) : (
                <FaSave size={14} />
              )}
              {isFetchingStock
                ? "Loading Stock…"
                : isEditMode
                  ? "Save Changes"
                  : "Confirm Production"}
            </button>
          </div>
        </div>
      </div>

      {/* Render the Super Detail Modal if active */}
      {detailModalTag && (
        <ConsumptionDetailModal
          tag={detailModalTag.tag}
          resolution={detailModalTag.resolution}
          onClose={() => setDetailModalTag(null)}
        />
      )}

      {showChangeItemForm && (
        <ChangeItemFormModal
          isOpen={showChangeItemForm}
          onClose={closeChangeItemForm}
          selectedItemToChange={selectedItemToChange}
          activeBom={activeBom} setActiveBom={setActiveBom}
          productBom={productBom} setProductBom={setProductBom}
        />
      )}
    </>
  );
};

const ChangeItemFormModal = ({
  isOpen, onClose,
  selectedItemToChange,
  activeBom, setActiveBom,
  productBom, setProductBom,
}) => {
  const [loading, setLoading] = useState(false);
  const [itemList, setItemList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredTerms, setFilteredTerms] = useState([]);
  
  // Local state to track selection before clicking "Select" button
  const [tempSelection, setTempSelection] = useState(null);

  const resetVariables = () => {
    setLoading(false);
    setCurrentPage(1);
    setSearchTerm("");
    setFilteredTerms([]);
    setTempSelection(null); 
  };

  const fetchItemListData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${domain}/api/Products/by-item-type-category?category=Regular`, {
        headers: { 'Content-Type': 'application/json' }
      });
      const finalData = response.data?.filter(i => i.baseUomName === selectedItemToChange.uomName)
      setItemList(finalData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedItemToChange]);

  useEffect(() => {
    if (isOpen) {
      fetchItemListData();

      const foundDataComponent = activeBom?.components?.find(i => i.componentProductId?.toString() === selectedItemToChange?.componentProductId?.toString())
      const _item = {
        id: foundDataComponent?.componentProductId ?? null,
        itemCode: foundDataComponent?.componentItemCode ?? null,
        productName: foundDataComponent?.componentProductName ?? null,
        brandName: foundDataComponent?.componentProductBrandName ?? null,
        baseUomName: foundDataComponent?.uomName ?? null,
      }
      // Initialize local selection with the currently selected item from props
      setTempSelection(_item);
    }
  }, [isOpen, fetchItemListData, activeBom?.components, selectedItemToChange]);

  const handleClose = () => {
    onClose();
    resetVariables();
  };

  // Logic when the "Select" button is clicked
  const handleConfirmSelection = () => {
    if (tempSelection) {
      setActiveBom(prev => ({
        ...prev,
        components: prev.components.map(component =>
          component.componentProductId?.toString() ===
          selectedItemToChange?.componentProductId?.toString()
            ? {
                ...component,
                componentItemCode: tempSelection.itemCode ?? null,
                componentProductId: tempSelection.id ?? null,
                componentProductName: tempSelection.productName ?? null,
                componentProductBrandName: tempSelection.brandName ?? null,
              }
            : component
        ),
      }));

      setProductBom(prev => ({
        ...prev,
        components: prev.components.map(component =>
          component.componentProductId?.toString() ===
          selectedItemToChange?.componentProductId?.toString()
            ? {
                ...component,
                componentItemCode: tempSelection.itemCode ?? null,
                componentProductId: tempSelection.id ?? null,
                componentProductName: tempSelection.productName ?? null,
                componentProductBrandName: tempSelection.brandName ?? null,
              }
            : component
        ),
      }));


      handleClose();
    }
  };

  useEffect(() => {
    const lowerSearch = searchTerm.toLowerCase();
    const results = itemList.filter((item) =>
      (item.productName || "").toLowerCase().includes(lowerSearch) ||
      (item.itemCode || "").toLowerCase().includes(lowerSearch) ||
      (item.brandName || "").toLowerCase().includes(lowerSearch)
    );
    setFilteredTerms(results);
    setCurrentPage(1);
  }, [searchTerm, itemList]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTerms.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-900/70 backdrop-blur-sm p-2 sm:p-4 lg:p-8">
      <div className="flex h-full max-h-[95vh] w-full max-w-[1000px] flex-col rounded-2xl bg-white shadow-2xl overflow-hidden border border-gray-200">
        
        {/* HEADER */}
        <div className="flex items-center justify-between border-b border-gray-100 bg-white px-4 py-3 sm:px-6 sm:py-4 shrink-0">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white shadow-lg bg-emerald-600 shadow-emerald-200">
              <Package size={17} />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-gray-900 leading-none">Select Item</h2>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Choose an item from the list</p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 text-gray-400 hover:text-red-500 transition-colors bg-gray-50 hover:bg-red-50 rounded-lg">
            <FaTimes size={18} />
          </button>
        </div>

        {/* SEARCH */}
        <div className="p-3 sm:p-4 bg-white border-b border-slate-50">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search item code, name, brand..."
              className="w-full pl-10 pr-4 py-2.5 text-sm border-none ring-1 ring-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all shadow-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        
        {/* SCROLLABLE BODY */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 bg-slate-50/50">
          {loading ? (
            <div className="flex justify-center items-center py-20"><Loader className="animate-spin text-emerald-600" /></div>
          ) : (
            <>
              {/* DESKTOP TABLE */}
              <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <table className="w-full text-sm text-left text-slate-600">
                  <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-4 font-semibold w-10 text-center">Select</th>
                      <th className="px-6 py-4 font-semibold">Item Code</th>
                      <th className="px-6 py-4 font-semibold">Product Name</th>
                      <th className="px-6 py-4 font-semibold">Brand Name</th>
                      <th className="px-6 py-4 font-semibold">UOM</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {currentItems.map((item) => {
                      const isSelected = tempSelection?.id === item.id;
                      return (
                        <tr 
                          key={item.id} 
                          onClick={() => setTempSelection(item)}
                          className={`cursor-pointer transition-colors ${
                            isSelected ? 'bg-emerald-50/60' : 'hover:bg-slate-50'
                          }`}
                        >
                          <td className="px-6 py-4 text-center">
                            <div className={`mx-auto w-5 h-5 rounded-full border flex items-center justify-center transition-all ${
                              isSelected ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300 bg-white'
                            }`}>
                              {isSelected && <Check size={12} strokeWidth={4} />}
                            </div>
                          </td>
                          <td className={`px-6 py-4 font-bold ${isSelected ? 'text-emerald-700' : 'text-indigo-600'}`}>
                            {item.itemCode}
                          </td>
                          <td className="px-6 py-4 font-medium text-slate-800">{item.productName}</td>
                          <td className="px-6 py-4 text-slate-500">{item.brandName}</td>
                          <td className="px-6 py-4 text-slate-500">{item.baseUomName}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* MOBILE CARDS */}
              <div className="md:hidden flex flex-col gap-3">
                {currentItems.map((item) => {
                  const isSelected = tempSelection?.id === item.id;
                  return (
                    <div 
                      key={item.id}
                      onClick={() => setTempSelection(item)}
                      className={`p-4 rounded-xl border transition-all active:scale-[0.98] ${
                        isSelected 
                        ? 'bg-emerald-50 border-emerald-500 ring-1 ring-emerald-500' 
                        : 'bg-white border-slate-200'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className={`text-xs font-bold px-2 py-1 rounded ${
                          isSelected ? 'bg-emerald-200 text-emerald-800' : 'bg-indigo-50 text-indigo-600'
                        }`}>
                          {item.itemCode}
                        </span>
                        {isSelected && (
                          <span className="flex items-center gap-1 text-emerald-600 font-bold text-[10px] uppercase">
                            <Check size={12} strokeWidth={3} /> Selected
                          </span>
                        )}
                      </div>
                      <h3 className="text-sm font-bold text-slate-800 uppercase">{item.productName}</h3>
                      <p className="text-xs text-slate-500 mt-1">{item.brandName}</p>
                    </div>
                  );
                })}
              </div>

              {currentItems.length === 0 && (
                <div className="text-center py-20 text-slate-400 text-sm">
                  No items found matching "{searchTerm}"
                </div>
              )}
            </>
          )}
        </div>

        {/* FOOTER - Pagination and Action Button */}
        <div className="bg-white p-4 border-t border-slate-100 shrink-0 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="w-full sm:w-auto">
            <Pagination
              itemsPerPage={itemsPerPage}
              totalItems={filteredTerms.length}
              currentPage={currentPage}
              paginate={paginate}
            />
          </div>
          
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <button
              onClick={handleClose}
              className="flex-1 sm:flex-none px-6 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              disabled={!tempSelection}
              onClick={handleConfirmSelection}
              className={`flex-1 sm:flex-none px-10 py-2.5 text-sm font-bold rounded-xl shadow-lg transition-all flex items-center justify-center gap-2
                ${tempSelection 
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-emerald-200 active:scale-95' 
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none'
                }`}
            >
              <Check size={18} />
              Select
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AddProduction;
